import './egress-room.css'
import '@livekit/components-styles'
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRoomContext,
  useTracks,
  VideoTrack,
} from '@livekit/components-react'
import EgressHelper from '@livekit/egress-sdk'
import { Track } from 'livekit-client'
import { useEffect, useState } from 'react'

export function EgressRoom() {
  // Add egress-template class to body for scoped styles
  useEffect(() => {
    document.body.classList.add('egress-template')
    return () => {
      document.body.classList.remove('egress-template')
    }
  }, [])

  // Get connection details from URL parameters via EgressHelper
  const url = EgressHelper.getLiveKitURL()
  const token = EgressHelper.getAccessToken()
  const [error, setError] = useState<Error>()

  if (!url || !token) {
    return (
      <div className="flex h-screen items-center justify-center bg-black text-red-500 text-xl">
        missing required params url and token
      </div>
    )
  }

  return (
    <LiveKitRoom serverUrl={url} token={token} onError={setError}>
      {error ? (
        <div className="flex h-screen items-center justify-center bg-black text-red-500 text-xl">
          {error.message}
        </div>
      ) : (
        <CompositeTemplate />
      )}
    </LiveKitRoom>
  )
}

function CompositeTemplate() {
  const room = useRoomContext()

  EgressHelper.setRoom(room)

  // Start recording when agent video track has decoded frames
  useEffect(() => {
    const interval = setInterval(async () => {
      for (const p of Array.from(room.remoteParticipants.values())) {
        if (!p.isAgent) continue
        for (const pub of Array.from(p.trackPublications.values())) {
          if (pub.kind === Track.Kind.Video && pub.videoTrack) {
            const stats = await pub.videoTrack.getRTCStatsReport()
            if (stats) {
              const decoded = Array.from(stats).some(
                (item) => item[1].type === 'inbound-rtp' && item[1].framesDecoded > 0
              )
              if (decoded) {
                EgressHelper.startRecording()
                clearInterval(interval)
                return
              }
            }
          }
        }
      }
    }, 50)

    return () => clearInterval(interval)
  }, [room.remoteParticipants.values])

  // Get all video tracks
  const allTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], {
    onlySubscribed: true,
  })

  // Filter out local participant tracks
  const remoteTracks = allTracks.filter(
    (tr) => tr.participant.identity !== room.localParticipant.identity
  )

  // Find screenshare from user
  const userScreenShare = remoteTracks.find((tr) => tr.source === Track.Source.ScreenShare)

  // Find user camera (non-avatar)
  const userCamera = remoteTracks.find(
    (tr) => tr.source === Track.Source.Camera && !tr.participant.isAgent
  )

  // Find agent camera
  const agentCamera = remoteTracks.find(
    (tr) => tr.source === Track.Source.Camera && tr.participant.isAgent
  )

  return (
    <div className="h-screen w-screen bg-black">
      {/* Pitch Layout - 2/3 screenshare left, 1/3 cameras right */}
      <div className="flex h-full w-full gap-4 p-4">
        {/* Left side - Screen share (2/3) */}
        <div className="flex flex-2 items-center justify-center overflow-hidden rounded-xl bg-black">
          {userScreenShare && (
            <VideoTrack trackRef={userScreenShare} className="h-full w-full object-contain" />
          )}
        </div>

        {/* Right side - Cameras (1/3) */}
        <div className="flex flex-1 flex-col gap-4">
          {/* User camera */}
          <div className="aspect-4/3 w-full flex-none overflow-hidden rounded-xl">
            {userCamera && (
              <VideoTrack trackRef={userCamera} className="h-full w-full object-cover" />
            )}
          </div>

          {/* Agent camera */}
          <div className="aspect-4/3 w-full flex-none overflow-hidden rounded-xl">
            {agentCamera && (
              <VideoTrack trackRef={agentCamera} className="h-full w-full object-cover" />
            )}
          </div>

          {/* SeedForge logo - centered in remaining space */}
          <div className="flex flex-1 items-end justify-center px-8 overflow-hidden">
            <img src="/seedforge-full-light.svg" alt="SeedForge" className="h-full" />
          </div>
        </div>
      </div>
      <RoomAudioRenderer />
    </div>
  )
}
